import { ChangeEvent, FC, useEffect, useState } from "react";
import { map, size } from "lodash";

import { useCompanyContext, useSubscriptionContext } from "Bands/contexts";
import { CenterContent, Icon } from "shared/styles";
import {
  APIErrorMessage,
  Message,
  Button,
  Card,
  Loader,
} from "shared/components";
import { copy } from "shared/assets";
import { Band, Plus } from "shared/assets/icons";
import { Spacer } from "shared/layout";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { ContentWidth } from "shared/styles/constants";
import {
  BillingFormModal,
  BillingInfoSelect,
  SubscriptionSummary,
} from "Bands/pages/subscription/containers";
import { BAND_PATHS } from "Bands/router";
import { useBandRoute, useCreateBandSubscription } from "Bands/utils";

const billingInfoCopy = copy.subscription.billingInfo;

export const SubscriptionBillingInfo: FC = () => {
  const [showAddBillingModal, setShowAddBillingModal] = useState(false);

  const { userCompanies } = useCompanyContext();
  const {
    selectedBilling,
    selectedSubscription,
    chooseBillingMethod,
    handleSubscribe,
  } = useSubscriptionContext();

  const { apiMeta, customSubscriptionApiMeta } = useCreateBandSubscription();

  const { pushRoute } = useBandRoute();

  if (!selectedSubscription) {
    pushRoute(BAND_PATHS.SUBSCRIPTION_OVERVIEW);
  }

  const [selectedCompanyId, setSelectedCompanyId] =
    useState<string | null>(null);

  useEffect(() => {
    // if there's only 1 company, auto select that company
    if (userCompanies.length === 1) {
      const companyId = userCompanies[0].id;

      chooseBillingMethod(companyId);
      setSelectedCompanyId(companyId);
    }
  }, [chooseBillingMethod, userCompanies]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const companyId = event.target.value;

    chooseBillingMethod(companyId);
    setSelectedCompanyId(companyId);
  };

  return (
    <>
      {showAddBillingModal && (
        <BillingFormModal handleClose={() => setShowAddBillingModal(false)} />
      )}
      <CenterContent width={ContentWidth.wide}>
        <Spacer
          spaceDirection="horizontal"
          size="xl"
          flexWrap="nowrap"
          align="flex-start"
          mobile={{ spaceDirection: "vertical", size: "m" }}
        >
          <Card title={billingInfoCopy.title} icon={Band}>
            <Spacer size="l">
              <Spacer size="s">
                {size(userCompanies) === 0 && (
                  <Message
                    status={IStatus.warning}
                    message={billingInfoCopy.noBillingInfo}
                  />
                )}
                <Spacer size="l">
                  {map(userCompanies, (company) => (
                    <BillingInfoSelect
                      key={company.id}
                      company={company}
                      checked={company.id === selectedCompanyId}
                      onChange={handleChange}
                    />
                  ))}
                </Spacer>
                <Button
                  theme={ITheme.secondary}
                  onClick={() => setShowAddBillingModal(true)}
                  data-testid="add_billing_info_button"
                >
                  <Icon as={Plus} />
                  <span>{billingInfoCopy.cta}</span>
                </Button>
              </Spacer>
              <Button
                onClick={() => handleSubscribe()}
                disabled={!selectedBilling}
                data-testid="start_payment_button"
              >
                <span>{billingInfoCopy.next}</span>
                {(apiMeta?.status === IAPIState.pending ||
                  customSubscriptionApiMeta?.status === IAPIState.pending) && (
                  <Loader size="s" />
                )}
              </Button>
              {(apiMeta?.status === IAPIState.failed ||
                customSubscriptionApiMeta?.status === IAPIState.failed) && (
                <APIErrorMessage
                  apiError={apiMeta?.error}
                  apiErrorMessage={true}
                />
              )}
            </Spacer>
          </Card>
          <SubscriptionSummary />
        </Spacer>
      </CenterContent>
    </>
  );
};
